import { Icon, If, onMount, reactive, watch } from 'cdui-js';
import { CheckBox } from '../../../../components/CheckBox';
import { lastProcess, nextProcess, pState, setFormValidateFunc } from '../../store';
import { createW8ben, saveCareer } from '../../../../../../api/open-account';
import type { AgreementSceneDocItem } from '../../../../../../api/agreement-doc';
import { openAgreementDoc } from '../../../../../../lib/agreement-doc-view';
import { agreementLinkLabel } from '../../../../../../lib/agreementLinkLabel';
import { loadAgreementDocsByCodesWithPreload } from '../../../../../../lib/agreement-pdf-cache';
import { showToast } from '../../../../components/Toast';
import dayts from '@china-difi/lib/date';
import { LastNextBtns } from '../../components/LastNextBtns';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { busConfigsMaps, busConfigsTick } from '../../../../../../stores/bus-config';
import i18n from '../../../../../../i18n';
import { getTaxResidenceType, syncTaxFieldsFromLic } from './taxSync';
import { isOpenProgressReEdit } from '../../reEditMode';

const SCENE_OPEN_ACCOUNT_TAX_INFO = 'SCENE_OPEN_ACCOUNT_TAX_INFO';
/** W-8BEN表格 */
const DOC_W8BEN = 'AGR0017';
/** 自我证明协议 */
const DOC_SELF_CERT = 'AGR0016';

const getTaxJurisdictionLabel = () => {
  const residenceType = getTaxResidenceType();
  const T = i18n.OpenProgress.Info.Tax;
  const map: Record<string, string> = {
    MAINLAND_CHINA: T.TaxJurisdictionMainland,
    HONG_KONG: T.TaxJurisdictionHk,
    MACAO: T.TaxJurisdictionMacao,
  };
  if (residenceType === 'OTHER_REGION') {
    const code = pState.uploadLicForm.nationality;
    return (code && busConfigsMaps.countryList[code]) || '-';
  }
  return map[residenceType] ?? '-';
};

export const TaxInfo = (props) => {
  const state = reactive({
    checkW8: false,
    loading: false,
    w8ben: null as AgreementSceneDocItem | null,
    selfCert: null as AgreementSceneDocItem | null,
    dictRev: 0,
  });
  // 协议勾选 DOM 引用：校验失败时滚动到对应未勾选项
  let check1Ref: HTMLDivElement | null = null;
  let check2Ref: HTMLDivElement | null = null;

  watch(
    () => busConfigsTick.v,
    (v) => {
      state.dictRev = v;
    },
  );

  const taxJurisdictionLabel = () => {
    void state.dictRev;
    return getTaxJurisdictionLabel();
  };

  const validateFunc = async () => {
    if (!pState.taxCheck1) {
      showToast(i18n.OpenProgress.Info.Tax.ToastCheck1);
      check1Ref?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }

    if (!pState.taxCheck2) {
      showToast(i18n.OpenProgress.Info.Tax.ToastCheck2);
      check2Ref?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }

    return true;
  };

  onMount(() => {
    syncTaxFieldsFromLic();

    setFormValidateFunc({
      callback: validateFunc,
    });

    loadAgreementDocsByCodesWithPreload({
      sceneCode: SCENE_OPEN_ACCOUNT_TAX_INFO,
      docCodes: [DOC_W8BEN, DOC_SELF_CERT],
    })
      .then(({ docMap }) => {
        state.w8ben = docMap[DOC_W8BEN] ?? null;
        state.selfCert = docMap[DOC_SELF_CERT] ?? null;
      })
      .catch(() => {
        // 接口失败时保留 i18n 兜底文案，点击时 toast
      });
  });

  const openDoc = (doc: AgreementSceneDocItem | null) => {
    openAgreementDoc(doc);
  };

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-XS">
        {i18n.OpenProgress.Info.Tax.Title}
        {props.stepText}
      </div>

      <div class="font-s-S margin-b-L">{i18n.OpenProgress.Info.Tax.Intro}</div>

      <div class="flex-column gap-12px margin-b-XL">
        <div class="bg-c-D round padding">
          <div>{i18n.OpenProgress.Info.Tax.LabelTaxJurisdiction}</div>
          <div class="color-D font-s-S margin-t-S">{taxJurisdictionLabel()}</div>
        </div>

        <div class="bg-c-D round padding">
          <div>{i18n.OpenProgress.Info.Tax.LabelTaxNumber}</div>
          <div class="color-D font-s-S margin-t-S">{pState.uploadLicForm.idCardNo || '-'}</div>
        </div>
      </div>

      <div class="color-D font-s margin-b-XL">{i18n.OpenProgress.Info.Tax.SignTaxForm}</div>

      <div
        class="flex justify-space-between align-center width-100 border-b padding-y-XS"
        onclick={() => {
          openDoc(state.w8ben);
          if (state.w8ben) state.checkW8 = true;
        }}
      >
        <div class="flex align-center gap-8px">
          <svg width="25" height="25" viewBox="0 0 25 25" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="25" height="25" rx="4" fill="#FF303A" />
            <path
              d="M3.7 8.86H6.66C8.38667 8.86 9.25 9.59 9.25 11.05C9.25 12.5233 8.38 13.26 6.64 13.26H4.87V16H3.7V8.86ZM4.87 9.86V12.26H6.57C7.08333 12.26 7.46333 12.1667 7.71 11.98C7.95 11.7867 8.07 11.4767 8.07 11.05C8.07 10.63 7.94667 10.3267 7.7 10.14C7.46667 9.95333 7.09 9.86 6.57 9.86H4.87ZM9.85273 8.86H12.4627C13.6161 8.86 14.4927 9.18 15.0927 9.82C15.6661 10.4267 15.9527 11.2967 15.9527 12.43C15.9527 13.5567 15.6661 14.4267 15.0927 15.04C14.4927 15.68 13.6161 16 12.4627 16H9.85273V8.86ZM11.0227 9.86V15H12.2427C13.1294 15 13.7794 14.7933 14.1927 14.38C14.5927 13.96 14.7927 13.31 14.7927 12.43C14.7927 11.5233 14.5927 10.87 14.1927 10.47C13.7861 10.0633 13.1361 9.86 12.2427 9.86H11.0227ZM16.65 8.86H21.52V9.86H17.82V11.84H21.31V12.84H17.82V16H16.65V8.86Z"
              fill="white"
            />
          </svg>

          <div>{state.w8ben?.name || i18n.OpenProgress.Info.Tax.W8Ben}</div>
        </div>

        <Icon name="common-arrow-right"></Icon>
      </div>
      <FieldReEditHint field="w8_ben" />

      <div class="margin-t-S flex-column gap-16px">
        <div ref={(el) => { check1Ref = el; }}>
          <CheckBox
            type={2}
            class="padding-y-XS"
            active={pState.taxCheck1}
            change={(e) => {
              pState.taxCheck1 = e;
            }}
          >
            {i18n.OpenProgress.Info.Tax.Check1Before}
            {i18n.OpenProgress.Info.Tax.Check1After}
          </CheckBox>
        </div>

        <div ref={(el) => { check2Ref = el; }}>
          <CheckBox
            type={2}
            class="padding-y-XS"
            active={pState.taxCheck2}
            change={(e) => {
              pState.taxCheck2 = e;
            }}
          >
            {i18n.OpenProgress.Info.Tax.Check2Before}
            <span
              data-nav-only
              class="agreement-link"
              onclick={(e) => {
                e.stopPropagation();
                openDoc(state.selfCert);
              }}
            >
              {agreementLinkLabel(state.selfCert?.name, i18n.OpenProgress.Info.Tax.LinkSelfCert)}
            </span>
            {i18n.OpenProgress.Info.Tax.Check2After}
          </CheckBox>
        </div>
      </div>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          loading={state.loading}
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const valid = await validateFunc();
            if (!valid) return;

            try {
              state.loading = true;
              syncTaxFieldsFromLic();
              await createW8ben({
                id: pState.id,
                signature: pState.uploadLicForm.fullName,
                signatureDate: dayts.format(Date.now()),
              });
              await saveCareer({ ...pState.taxInfoForm, progress: 8 });
              nextProcess();
            } finally {
              state.loading = false;
            }
          }}
        />
      </If>
    </div>
  );
};
